import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiltersProps } from "./DirectoryFilters";
import { locationService } from "@/services/locationListingService";
import { Location, LocationResponse } from "@/types/location";
import { Badge } from "@/components/ui/badge";
import { Star, Phone, Image as ImageIcon, ChevronLeft, ChevronRight, Globe2, MapPin, Eye, Flame, Droplets, Heart, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import SaunaImagePlaceholder from "./SaunaImagePlaceholder";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface DirectoryListViewProps {
  filters: FiltersProps;
  columns?: number;
  onResultsChange?: (hasResults: boolean, count?: number) => void;
}

const DirectoryListView: React.FC<DirectoryListViewProps> = ({ 
  filters, 
  columns = 6, // Increased default columns for more listings
  onResultsChange 
}) => {
  const [listings, setListings] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 24, // Increased per page for more listings
    total: 0,
    total_pages: 0
  });
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  // Helper function to safely render country data
  const renderCountry = (country: any): string => {
    try {
      if (typeof country === 'string') {
        return country;
      } else if (country && typeof country === 'object') {
        // Don't log every country object - only log if there's an issue
        return country.name || country.code || 'Ireland';
      } else {
        return 'Ireland';
      }
    } catch (error) {
      console.error('Error rendering country:', error, country);
      return 'Ireland';
    }
  };

  // Helper function to safely render any data
  const safeRender = (data: any, fallback: string = ''): string => {
    try {
      if (typeof data === 'string') {
        return data;
      } else if (data && typeof data === 'object') {
        // Handle common object properties
        if (data.name) return data.name;
        if (data.code) return data.code;
        if (data.title) return data.title;
        if (data.label) return data.label;
        // If it's an array, join it
        if (Array.isArray(data)) return data.join(', ');
        // For other objects, try to get a meaningful string representation
        return fallback;
      } else if (data === null || data === undefined) {
        return fallback;
      } else {
        return String(data);
      }
    } catch (error) {
      return fallback;
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    const fetchFilters = {
      country: filters.selectedCountry,
      county: filters.selectedCounties?.join(",") || "",
      type: filters.type !== 'all' ? filters.type : "",
      search: filters.search || "",
      serviceTypes: filters.serviceTypes,
      placeTypes: filters.placeTypes,
      experiences: filters.experiences,
      amenities: filters.amenities,
      showAll: filters.showAll,
      location: filters.location,
      page: !filters.showAll ? pagination.page : 1,
      per_page: !filters.showAll ? pagination.per_page : 24
    };

    locationService.getLocations(fetchFilters)
      .then((response: LocationResponse) => {
        setListings(response.data);
        setPagination({
          page: response.meta.current_page,
          per_page: response.meta.per_page,
          total: response.meta.total,
          total_pages: response.meta.last_page
        });
        setLoading(false);
        if (onResultsChange) onResultsChange(response.data.length > 0, response.meta.total);
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
        setError("Failed to load locations. Please try again.");
        setLoading(false);
        setListings([]);
        if (onResultsChange) onResultsChange(false, 0);
      });
  }, [filters, pagination.page, pagination.per_page, onResultsChange]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getPlanBadge = (plan: string) => {
    switch(plan) {
      case 'premium':
        return <Badge className="bg-sauna-orange text-white">Premium</Badge>;
      case 'basic':
        return <Badge className="bg-sauna-blue text-white">Basic</Badge>;
      default:
        return <Badge className="text-gray-600 border-gray-400">Free</Badge>;
    }
  };

  const getGridCols = () => {
    switch (columns) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-1 sm:grid-cols-2";
      case 3:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
      case 4:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
      case 5:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";
      case 6:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6";
      default:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6";
    }
  };

  // Helper function to get the best image for a location
  const getLocationImage = (item: Location): string => {
    if (item.images && item.images.length > 0) {
      return item.images[0];
    }
    if (item.image) {
      return item.image;
    }
    if (item.galleryImages && item.galleryImages.length > 0) {
      return item.galleryImages[0];
    }
    return '';
  };

  // Handle image error
  const handleImageError = (itemId: string) => {
    setImageErrors(prev => new Set(prev).add(itemId));
  };

  return (
    <div className="space-y-6">
      {/* Error Boundary */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading directory</h3>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" variant="default" />
          <p className="mt-4 text-sauna-navy font-medium">Loading listings...</p>
        </div>
      )}

      {/* Results */}
      <div className={`grid gap-4 ${getGridCols()}`}>
        {listings.map((item, index) => {
          // Add comprehensive null checks
          if (!item) {
            console.error('Null item in locations array:', item);
            return null;
          }

          try {
            const imageUrl = getLocationImage(item);
            const itemId = typeof item.id === 'string' ? item.id : String(index);
            const hasImage = !!imageUrl && !imageErrors.has(itemId);

            return (
              <Link key={itemId} to={`/sauna/${itemId}`} className="block group">
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-sauna-orange/30 hover:scale-[1.02]">
                  {/* Top Section - Image Area */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {hasImage ? (
                      <img
                        src={imageUrl}
                        alt={safeRender(item.name, 'Sauna')}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={() => handleImageError(itemId)}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-sauna-orange/10 to-sauna-blue/10 flex items-center justify-center">
                        <div className="text-2xl font-bold text-sauna-navy">
                          {safeRender(item.name, 'SA').substring(0, 2).toUpperCase()}
                        </div>
                      </div>
                    )}
                    
                    {/* Save Button */}
                    <button 
                      className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-sauna-orange/10 transition-colors shadow-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        // TODO: Implement save to favourites
                        console.log('Save to favourites clicked');
                      }}
                    >
                      <Heart className="h-4 w-4 text-sauna-orange" />
                    </button>

                    {/* Rating Badge */}
                    {item.rating && (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-white/90 text-xs font-medium flex items-center gap-1">
                        <Star className="h-3 w-3 text-sauna-orange fill-current" />
                        {safeRender(item.rating, '0')}
                      </div>
                    )}
                  </div>
                  
                  {/* Bottom Section - Minimal Details */}
                  <div className="p-3">
                    {/* Title */}
                    <h3 className="font-semibold text-sauna-navy text-sm line-clamp-1 mb-1">
                      {safeRender(item.name, 'Unknown Location')}
                    </h3>
                    
                    {/* Location */}
                    <div className="flex items-center text-gray-500 text-xs mb-1">
                      <MapPin className="w-3 h-3 mr-1 text-sauna-orange" />
                      {safeRender(item.county, "County")}, {renderCountry(item.country)}
                    </div>
                    
                    {/* Price */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm font-medium text-sauna-orange">
                        <span className="mr-1">€</span>
                        {safeRender(item.price, String(Math.floor(Math.random() * 30) + 15))}
                        <span className="text-gray-500 ml-1 text-xs">/ session</span>
                      </div>
                      
                      {/* Type Badge */}
                      <div className="text-xs text-gray-500">
                        {(item.serviceTypes || []).includes('sauna') ? 'Sauna' : 'Wellness'}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          } catch (error) {
            console.error('Error rendering location item:', error, item);
            return null;
          }
        })}
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              variant="outline"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages}
              variant="outline"
              size="sm"
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((pagination.page - 1) * pagination.per_page) + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.per_page, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <Button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  variant="outline"
                  size="sm"
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </Button>
                
                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      variant={pagination.page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                
                <Button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.total_pages}
                  variant="outline"
                  size="sm"
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectoryListView;

